import { store } from '@redux/store';
import { selectModal } from 'layout/redux/selectors';
import modalService from 'layout/services/modalService';
import { userProfileActions } from 'features/userProfile';
import { closeModalSvg } from '../constants/svgIcons';
import { privacyPolicyParagraph } from '../constants/sharedHtml';
import { FIELD_CLASS_NAMES, SELECT_FIELD_CLASS_NAMES } from '../constants/fieldClassNames';
import leadConstructor from '../services/leadConstructor';
import { postLeadTourForm } from '../services/taskFlow';
import NameInput from './nameInput';
import EmailInput from './emailInput';
import PhoneInput from './phoneInput';
import ZipInput from './zipInput';
import RealtorDropdown from './realtorDropdown';
import ContactMethodDropdown from './contactMethodDropdown';
import DateCarousel from './dateCarousel';
import LegalText from './legalText';

/**
 * "Request a tour" form — reached by clicking "Book appointment" inside the
 * already-open RequestInformationEntry. Sibling of GetMoreInformationForm: same
 * field/validation/submission machinery and nhs-c-input__* presentation, plus a DateCarousel
 * field. Per the design, no community/agent/hours info block. A dead end — no back button, only
 * the close (X).
 */
export default class RequestATourForm {
  constructor() {
    const state = store.getState();
    const userProfile = state?.userProfile || {};
    const {
      id, guid, type, communityId, communityName, marketId, marketName, brandName, fromPage,
    } = selectModal(state) ?? {};

    this.id = id;
    this.guid = guid;
    this.type = type;
    this.communityId = communityId;
    this.communityName = communityName;
    this.marketId = marketId;
    this.marketName = marketName;
    this.brandName = brandName;
    this.fromPage = fromPage;
    this.excludeRecommendations = true;
    this.formId = 'requestATourForm';
    this.submitButtonId = 'requestATourSubmitButton';

    this.fullNameInput = new NameInput();
    this.fullNameInput.value = userProfile.name;
    this.fullNameInput
      .setClassNames(FIELD_CLASS_NAMES)
      .setLabelText('First and last name')
      .setInputId('requestATourName')
      .setErrorMessageId('nhs_ErrorForRequestATourName');

    this.emailAddressInput = new EmailInput();
    this.emailAddressInput.value = userProfile.email;
    this.emailAddressInput
      .setClassNames(FIELD_CLASS_NAMES)
      .setLabelText('Email address')
      .setInputId('requestATourEmail')
      .setErrorMessageId('nhs_ErrorForRequestATourEmail');

    this.phoneNumberInput = new PhoneInput();
    this.phoneNumberInput.value = userProfile.phone;
    this.phoneNumberInput
      .setClassNames(FIELD_CLASS_NAMES)
      .setLabelText('Phone number')
      .setInputId('requestATourPhone')
      .setErrorMessageId('nhs_ErrorForRequestATourPhone');

    this.postalCodeInput = new ZipInput();
    this.postalCodeInput.value = userProfile.zip;
    this.postalCodeInput.isInternational = userProfile.livesOutside;
    this.postalCodeInput
      .setClassNames({
        ...FIELD_CLASS_NAMES,
        checkboxWrapperClassName: 'nhs-c-input__container--with-text',
        checkboxClassName: 'nhs-c-input__check-box',
        checkboxLabelClassName: '',
      })
      .setLabelText('Your ZIP code')
      .setErrorMessageId('nhs_ErrorForRequestATourZip');

    this.contactMethodDropdown = new ContactMethodDropdown();
    this.contactMethodDropdown.value = userProfile.preferredContactMethod;
    this.contactMethodDropdown.setClassNames(SELECT_FIELD_CLASS_NAMES);

    this.realtorDropdown = new RealtorDropdown();
    this.realtorDropdown.value = userProfile.realtorQuestion;
    this.realtorDropdown
      .setClassNames(SELECT_FIELD_CLASS_NAMES)
      .setInputId('requestATourRealAgent');

    this.dateCarousel = new DateCarousel();

    this.legalText = new LegalText(privacyPolicyParagraph, [
      { key: '{0}', value: 'Submit request' },
      { key: '{1}', value: this.brandName ?? 'the builder' },
    ]);
    this.legalText.setId('requestATourLegalText');
    this.legalText.setTextClassName('nhs-o-typography--sans-label-1-extra-small-regular');
  }

  /**
   * Gets the form container.
   * @returns {HTMLElement}
   */
  getForm() {
    return document.getElementById(this.formId);
  }

  getSubmitButton() {
    return document.getElementById(this.submitButtonId);
  }

  static closeModal() {
    modalService.clearHtml();
  }

  /**
   * Validates every field, then builds and posts the lead. Mirrors
   * GetMoreInformationForm.submitForm — same lead flow, "-tour" suffix and DateCarousel instead
   * of "-information" and no date field.
   */
  async submitForm() {
    const fieldValidations = [
      { input: this.fullNameInput, wrapperKey: 'fullName' },
      { input: this.emailAddressInput, wrapperKey: 'email' },
      { input: this.phoneNumberInput, wrapperKey: 'phone' },
      { input: this.postalCodeInput, wrapperKey: 'zip' },
      { input: this.contactMethodDropdown, wrapperKey: 'contactMethod' },
      { input: this.realtorDropdown, wrapperKey: 'realtor' },
      { input: this.dateCarousel, wrapperKey: 'date' },
    ];

    const submitButton = this.getSubmitButton();
    submitButton.disabled = true;

    const results = await Promise.all(fieldValidations.map(({ input }) => input.validate()));

    fieldValidations.forEach(({ wrapperKey }, index) => {
      const wrapper = this.getForm().querySelector(`[data-field-wrapper="${wrapperKey}"]`);
      wrapper?.classList.toggle('nhs-c-input__wrapper--error', !results[index]);
    });

    if (results.some((isValid) => !isValid)) {
      submitButton.disabled = false;
      return;
    }

    // Only a validated lead is worth persisting for prefill — see bindChangeEvents() for why this
    // isn't dispatched on every field change instead.
    document.body.dispatchEvent(new CustomEvent('user-data-changed', {
      detail: { form: this.getForm() },
    }));

    const formData = new FormData(this.getForm());
    const leadInformation = Object.fromEntries(formData.entries());

    leadInformation.id = this.id;
    leadInformation.type = `${this.type}-tour`;
    leadInformation.excludeRecommendations = this.excludeRecommendations;
    leadInformation.fromPage = this.fromPage;
    leadInformation.guid = this.guid;

    const leadObject = leadConstructor(leadInformation);
    leadObject?.setHousingInformation?.({
      communityId: this.communityId,
      communityName: this.communityName,
      marketId: this.marketId,
      marketName: this.marketName,
      brandName: this.brandName,
    });

    postLeadTourForm(leadObject);
  }

  /**
   * Marks the phone field as required when the user's preferred contact method needs a phone
   * number to act on (Phone or Text Message) — same rule GetMoreInformationForm applies.
   * @param {string} value
   */
  setRequiredAttributeInPhone(value) {
    if (value === 'Phone' || value === 'Text Message') {
      this.phoneNumberInput.setRequired();
      return;
    }

    this.phoneNumberInput.removeRequired();
  }

  /**
   * Binds the events needed so the form can respond to user interactions.
   */
  bindEvents() {
    const closeElements = document.querySelectorAll('[data-close]');
    closeElements.forEach((element) => element.addEventListener('click', RequestATourForm.closeModal));

    this.phoneNumberInput.bindEvents();
    this.postalCodeInput.bindEvents();
    this.legalText.bindEvents();
    this.dateCarousel.bindEvents();
    this.realtorDropdown.setSelectedValue();
    this.contactMethodDropdown.setSelectedValue();

    this.setRequiredAttributeInPhone(this.contactMethodDropdown.value);
    this.contactMethodDropdown.getOnChangeEvent().subscribe(({ preferredContactMethod }) => {
      this.setRequiredAttributeInPhone(preferredContactMethod);
    });

    if (this.fullNameInput.value === '') {
      this.fullNameInput.focus();
    }

    this.onSubmitClick = this.submitForm.bind(this);
    this.getSubmitButton().addEventListener('click', this.onSubmitClick);

    this.bindChangeEvents();
  }

  /**
   * bindChangeEvents: subscribes every input's OnChangeEvent so any field change dispatches its
   * value into userProfileActions.update, matching informationFormPaid.js/brochureFormPaid.js/
   * askQuestionFormPaid.js — this is what the constructor's
   * userProfile.preferredContactMethod/realtorQuestion reads back from on reopen. dateCarousel's
   * emitted data has no matching userProfile field, so the update is a no-op for it.
   *
   * Deliberately does NOT also persist to the legacy prefillForm module
   * (GlobalResources14/Default/js/modules/prefillForm.js) on every keystroke/blur — that only
   * happens once, in submitForm(), once the lead is actually valid. See submitForm()'s
   * 'user-data-changed' dispatch.
   */
  bindChangeEvents() {
    const changeInputs = [
      this.emailAddressInput,
      this.fullNameInput,
      this.postalCodeInput,
      this.phoneNumberInput,
      this.contactMethodDropdown,
      this.realtorDropdown,
      this.dateCarousel,
    ];

    changeInputs.forEach((input) => input.getOnChangeEvent().subscribe((data) => {
      store.dispatch(userProfileActions.update(data));
    }));
  }

  /**
   * Removes the bindings that should not be still using.
   */
  unbindEvents() {
    const closeElements = document.querySelectorAll('[data-close]');
    closeElements.forEach((element) => element.removeEventListener('click', RequestATourForm.closeModal));
    this.getSubmitButton()?.removeEventListener('click', this.onSubmitClick);
    this.legalText.unbindEvents();
    this.dateCarousel.unbindEvents();
  }

  /**
   * @returns the HTML that represents the "Request a tour" form.
   */
  getHtml() {
    return `
    <div id="${this.formId}-wrapper" data-lead-form>
      <div class="nhs-o-flex-row-items-center nhs-u-gap--200 nhs-u-padding--600">
        <p class="nhs-u-flex--1-0-0 nhs-o-typography--souvenir-heading-1-extra-large-light">Request a tour</p>
        <button type="button" class="nhs-n1-c-button__modal-close nhs-n1-c-button__modal-close--transparent" data-close aria-label="Close modal">
          ${closeModalSvg}
        </button>
      </div>
      <div class="nhs-o-flex-column-items-start-self-stretch nhs-u-gap--600 nhs-u-padding--0-600-600">
        <form id="${this.formId}" class="nhs-u-width--full">
          <div class="nhs-o-flex-column-items-start-self-stretch nhs-u-gap--600">
            <div class="nhs-c-input__wrapper nhs-u-gap--300" data-field-wrapper="fullName">${this.fullNameInput.getHtml()}</div>
            <div class="nhs-c-input__wrapper nhs-u-gap--300" data-field-wrapper="email">${this.emailAddressInput.getHtml()}</div>
            <div class="nhs-c-input__wrapper nhs-u-gap--300" data-field-wrapper="phone">${this.phoneNumberInput.getHtml()}</div>
            <div class="nhs-c-input__wrapper nhs-u-gap--300" data-field-wrapper="zip">${this.postalCodeInput.getFieldHtml()}</div>
            ${this.postalCodeInput.getCheckboxHtml()}
            <div class="nhs-c-input__wrapper nhs-u-gap--300" data-field-wrapper="contactMethod">${this.contactMethodDropdown.getHtml()}</div>
            <div class="nhs-c-input__wrapper nhs-u-gap--300" data-field-wrapper="realtor">${this.realtorDropdown.getHtml()}</div>
            <div class="nhs-c-input__wrapper nhs-u-gap--300" data-field-wrapper="date">${this.dateCarousel.getHtml()}</div>
          </div>
          <input type="hidden" name="leadAction" value="ra" />
        </form>
        <button type="button" id="${this.submitButtonId}" class="nhs-c-button nhs-c-button--medium-40 nhs-c-button--primary-lead nhs-u-width--full" data-qa="request-a-tour-button">
          Submit request
        </button>
        ${this.legalText.getHtml()}
      </div>
    </div>`;
  }
}
